// Public signature portal — esign.com-style flow.
//
// Layout:
//   1. Document is rendered inline at the top (PDF in iframe / image inline).
//   2. Below that, the signature canvas — agent already showed what to sign.
//   3. On submit, backend stamps the signature into the original PDF and
//      returns a download URL for the SIGNED PDF, which we show on success.

import { useEffect, useRef, useState } from 'react';
import { CheckCircle, FileText, AlertCircle, Eraser, Download } from 'lucide-react';
import { viewSignaturePublic, submitSignaturePublic } from '../api.js';
import { toast } from './Toast.jsx';
import { preguntasSueltas } from '../marcadorDePregunta.js';
import { camposUsadosEn, inicialesDe, MAX_NOMBRE_DEL_FIRMANTE } from '../camposDeFirma.js';
// La cabecera, la hoja con sus preguntas clavadas y el fondo de esta página
// viven en PaginaDelDocumento.jsx: la vista previa a pantalla completa del
// módulo de Firmas usa las MISMAS piezas (regla 208).
import { CabeceraDeLaFirma, LetterPreview, FONDO_DE_LA_PAGINA, ANCHO_DE_LA_PAGINA } from './PaginaDelDocumento.jsx';
// El PDF con los sitios donde se va a estampar la firma: las mismas piezas que
// el editor de la agente (Lissette 2026-10-23, regla 208).
import { PdfConSitios } from './SitiosDeFirmaEnPdf.jsx';
import { esPdf } from '../camposSobreElPdf.js';


// Type-to-sign font choices. We avoid loading external webfonts (keeps the CSP
// untouched and works offline) by using cross-platform script-font stacks that
// fall back to the generic `cursive` keyword, so each option renders as a
// distinct handwriting style on whatever device the recipient signs from.
const TYPE_FONTS = [
  { id: 'f1', label: 'Style 1', css: '"Brush Script MT","Segoe Script","Bradley Hand",cursive' },
  { id: 'f2', label: 'Style 2', css: '"Lucida Handwriting","Apple Chancery","Segoe Script",cursive' },
  { id: 'f3', label: 'Style 3', css: '"Snell Roundhand","Ink Free","Brush Script MT",cursive' },
];
const fontCssFor = (id) => (TYPE_FONTS.find((f) => f.id === id) || TYPE_FONTS[0]).css;

export function SignaturePortalPage({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [signedDocUrl, setSignedDocUrl] = useState(null);
  // Signing method: 'draw' (canvas) keeps the original behaviour as default;
  // 'type' renders the typed name (in the chosen cursive font) to a PNG at
  // submit time, so the upload/stamp pipeline stays identical for both.
  const [mode, setMode] = useState('draw');
  const [typedName, setTypedName] = useState('');
  const [typedFontId, setTypedFontId] = useState('f1');
  // Respuestas a las preguntas del documento — Wendy Cercio (INSURANCE LATINO),
  // 2026-08-24: "quiero que el cliente ademas de firmar pueda responder si o no
  // a algunas preguntas". Van ENCIMA de la caja de firma: se contesta y luego
  // se firma, no al reves.
  const [responses, setResponses] = useState({});
  const [missing, setMissing] = useState([]);
  // NOMBRE E INICIALES, si la plantilla los pide (Lissette 2026-10-23: firmas
  // en varios lados, iniciales y nombre y apellido). Se escriben UNA vez y salen
  // en cada sitio marcado. Las iniciales se proponen solas a partir del nombre.
  const [signerName, setSignerName] = useState('');
  // null = automaticas (del nombre); en cuanto el cliente las toca, las suyas.
  const [initialsTyped, setInitialsTyped] = useState(null);
  const canvasRef = useRef(null);
  const hasInkRef = useRef(false);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    setLoading(true);
    viewSignaturePublic(token)
      .then((d) => {
        setData(d);
        if (d.status === 'SIGNED') setDone(true);
      })
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return; // not mounted in Type mode
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2.5;
    ctx.strokeStyle = '#0a1628';
    // (Re)initialising the canvas wipes any pixels, so the ink flag must reset
    // too — otherwise toggling Draw→Type→Draw could submit an empty canvas.
    hasInkRef.current = false;
  }, [data, done, mode]);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches?.[0] || e.changedTouches?.[0];
    const x = (t ? t.clientX : e.clientX) - rect.left;
    const y = (t ? t.clientY : e.clientY) - rect.top;
    return { x, y };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = getPos(e);
  };
  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(x, y);
    ctx.stroke();
    lastRef.current = { x, y };
    hasInkRef.current = true;
  };
  const end = () => { drawingRef.current = false; };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const ratio = window.devicePixelRatio || 1;
    ctx.clearRect(0, 0, canvas.width / ratio, canvas.height / ratio);
    hasInkRef.current = false;
  };

  // Render the typed name (in the chosen cursive font) to an offscreen canvas
  // and hand back a PNG blob — same format the drawn path produces, so the
  // backend stamp/upload is untouched. Font auto-shrinks to fit the width.
  // Las iniciales usan lo mismo, en un lienzo mas pequeño.
  const renderTypedSignatureBlob = (texto = typedName, w = 600, h = 200) => new Promise((resolve) => {
    const name = String(texto || '').trim();
    if (!name) { resolve(null); return; }
    const ratio = window.devicePixelRatio || 1;
    const c = document.createElement('canvas');
    c.width = w * ratio; c.height = h * ratio;
    const ctx = c.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.fillStyle = '#0a1628';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const css = fontCssFor(typedFontId);
    let size = 72;
    ctx.font = `${size}px ${css}`;
    while (size > 18 && ctx.measureText(name).width > w - 48) { size -= 2; ctx.font = `${size}px ${css}`; }
    ctx.fillText(name, w / 2, h / 2);
    c.toBlob(resolve, 'image/png');
  });

  const questions = Array.isArray(data?.questions) ? data.questions : [];
  // Lo que pide el documento: sale del cuerpo, igual que lo mira el servidor.
  const campos = data?.bodyText ? camposUsadosEn(data.bodyText) : [];
  // Y los sitios marcados sobre el PDF original (Lissette 2026-10-23). El
  // servidor los vuelve a exigir al firmar, contra la foto guardada.
  const sitiosDelPdf = Array.isArray(data?.pdfFields) && data.documentUrl && esPdf(data.documentMime) ? data.pdfFields : [];
  const pideNombre = campos.includes('nombre') || sitiosDelPdf.some((c) => c.tipo === 'nombre');
  const pideIniciales = campos.includes('iniciales') || sitiosDelPdf.some((c) => c.tipo === 'iniciales');
  const iniciales = (initialsTyped ?? inicialesDe(signerName || typedName)).trim().toUpperCase().slice(0, 4);
  // Las que NO llevan marcador en el cuerpo: son las unicas que salen en el
  // bloque de abajo. Las clavadas ya salen en su sitio dentro del documento.
  const preguntasFuera = preguntasSueltas(questions, data?.bodyText);
  const setAnswer = (id, value) => {
    setResponses((r) => ({ ...r, [id]: value }));
    setMissing((m) => m.filter((x) => x !== id));
  };
  /**
   * Las obligatorias sin contestar. Se comprueba tambien en el servidor —
   * esto es solo para poder senalarlas en pantalla ANTES de mandar nada, en
   * vez de que el cliente firme y le salte un error despues.
   */
  const missingRequired = () => questions
    .filter((q) => q.required)
    .filter((q) => {
      const v = responses[q.id];
      return q.type === 'checkbox' ? v !== true : !String(v ?? '').trim();
    })
    .map((q) => q.id);

  const submit = async () => {
    const faltan = missingRequired();
    if (faltan.length) {
      setMissing(faltan);
      toast.warn(faltan.length === 1
        ? 'Please answer the highlighted question.'
        : `Please answer the ${faltan.length} highlighted questions.`);
      // Llevar la vista a la primera sin contestar: en un cuestionario largo,
      // un aviso arriba y la pregunta abajo no se encuentran.
      try { document.getElementById(`q-${faltan[0]}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch { /* noop */ }
      return;
    }
    if (pideNombre && signerName.trim().length < 2) {
      toast.warn('Please type your full name. / Escriba su nombre y apellido.');
      try { document.getElementById('firma-nombre')?.focus(); } catch { /* noop */ }
      return;
    }
    if (pideIniciales && !iniciales) {
      toast.warn('Please type your initials. / Escriba sus iniciales.');
      try { document.getElementById('firma-iniciales')?.focus(); } catch { /* noop */ }
      return;
    }
    if (mode === 'type' && !typedName.trim()) {
      toast.warn('Please type your name to sign.');
      return;
    }
    if (mode === 'draw' && !hasInkRef.current) {
      toast.warn('Please sign in the box above first.');
      return;
    }
    setSubmitting(true);
    try {
      const blob = mode === 'type'
        ? await renderTypedSignatureBlob()
        : await new Promise((resolve) => canvasRef.current.toBlob(resolve, 'image/png'));
      if (!blob) throw new Error('Could not capture signature');
      const initialsBlob = pideIniciales ? await renderTypedSignatureBlob(iniciales, 300, 120) : null;
      if (pideIniciales && !initialsBlob) throw new Error('Could not capture initials');
      const result = await submitSignaturePublic(token, blob, questions.length ? responses : undefined, {
        initialsBlob,
        signerName: pideNombre ? signerName.trim() : undefined,
      });
      if (result?.signedDocUrl) setSignedDocUrl(result.signedDocUrl);
      setDone(true);
    } catch (e) {
      toast.error('Could not submit: ' + e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <CenteredMessage>Loading…</CenteredMessage>;
  if (err || !data) return (
    <CenteredMessage>
      <h2 style={{ margin: '0 0 .5rem', color: '#ff3d6b' }}>Link not valid</h2>
      <p>This signature link may have expired or been removed. Please contact your agent.</p>
    </CenteredMessage>
  );

  const expired = data.expiresAt && new Date(data.expiresAt) < new Date();

  return (
    <div style={{ minHeight: '100vh', background: FONDO_DE_LA_PAGINA, color: '#e6f0fa', padding: '1.5rem 1rem 3rem' }}>
      <div style={{ maxWidth: ANCHO_DE_LA_PAGINA, margin: '0 auto' }}>
        <CabeceraDeLaFirma data={data} />

        {expired && !done ? (
          <div style={{ background: 'rgba(255,61,107,.1)', border: '1px solid #ff3d6b', borderRadius: 12, padding: '1rem', display: 'flex', alignItems: 'center', gap: '.6rem' }}>
            <AlertCircle size={18} style={{ color: '#ff3d6b' }} />
            <div><strong style={{ color: '#ff3d6b' }}>This link has expired.</strong> Contact your agent for a new one.</div>
          </div>
        ) : done ? (
          <SuccessScreen signedDocUrl={signedDocUrl} originalDocUrl={data.documentUrl} />
        ) : (
          <>
            {/* Template letter takes precedence — that's the document. If only
                a file was attached (no template), fall back to the iframe. */}
            {/* Un PDF con sitios marcados ES el documento: se ve con cada sitio
                resaltado, que es exactamente donde va a salir lo que firme. */}
            {sitiosDelPdf.length > 0
              ? <PdfConSitios url={data.documentUrl} campos={sitiosDelPdf} lang="en"
                              valores={{ nombre: signerName.trim(), iniciales, fuente: fontCssFor(typedFontId), respuestas: responses, datos: data.pdfDatos || {} }} />
              : data.bodyText
              ? <LetterPreview body={data.bodyText} title={data.documentName} logoUrl={data.agencyLogoUrl} formatting={data.formatting}
                               questions={questions} responses={responses} missing={missing} onAnswer={setAnswer}
                               campos={{ nombre: signerName.trim(), iniciales, fuente: fontCssFor(typedFontId) }} />
              : data.documentUrl && <DocumentPreview url={data.documentUrl} mime={data.documentMime} />}

            {/* Preguntas del documento — ENCIMA de la firma (Wendy, 2026-08-24).
                Se contesta y luego se firma: firmar primero y contestar
                despues invertiria lo que significa la rubrica. */}
            {/* SOLO LAS QUE NO ESTAN CLAVADAS en el documento. Una pregunta
                que ya sale en su sitio dentro del texto no puede salir tambien
                aqui: se leeria como si se preguntara dos veces, y el cliente
                contestaria una y dejaria la otra en blanco.

                Las plantillas de ANTES de este cambio no tienen ningun
                marcador, asi que caen todas aqui y siguen funcionando
                exactamente igual que hasta ahora. */}
            {preguntasFuera.length > 0 && (
              <div style={{ marginTop: '1.5rem', background: 'rgba(52,211,153,.06)', border: '1px solid rgba(52,211,153,.3)', borderRadius: 14, padding: '1rem 1.1rem' }}>
                <strong style={{ fontSize: 13, color: '#34d399', textTransform: 'uppercase', letterSpacing: '.5px' }}>
                  Please answer before signing
                </strong>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '.9rem' }}>
                  {preguntasFuera.map((q) => (
                    <QuestionField
                      key={q.id}
                      q={q}
                      value={responses[q.id]}
                      onChange={(v) => setAnswer(q.id, v)}
                      missing={missing.includes(q.id)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* NOMBRE E INICIALES — solo si el documento los lleva marcados. Se
                escriben una vez y salen en cada sitio (Lissette 2026-10-23). */}
            {(pideNombre || pideIniciales) && (
              <div style={{ marginTop: '1.5rem', background: 'rgba(0,212,255,.05)', border: '1px solid rgba(0,212,255,.25)', borderRadius: 14, padding: '1rem 1.1rem' }}>
                <strong style={{ fontSize: 13, color: '#00d4ff', textTransform: 'uppercase', letterSpacing: '.5px' }}>
                  Your details / Sus datos
                </strong>
                <div style={{ fontSize: 12, color: '#7aa3c0', margin: '.3rem 0 .8rem' }}>
                  They appear in every marked spot of the document. / Salen en cada sitio marcado del documento.
                </div>
                <div style={{ display: 'flex', gap: '.8rem', flexWrap: 'wrap' }}>
                  {pideNombre && (
                    <label style={{ flex: '2 1 240px', fontSize: 12, color: '#7aa3c0' }}>
                      Full name / Nombre y apellido *
                      <input id="firma-nombre" value={signerName} maxLength={MAX_NOMBRE_DEL_FIRMANTE}
                        onChange={(e) => setSignerName(e.target.value)}
                        style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 4, padding: '.65rem .85rem', borderRadius: 10, border: '1px solid rgba(122,163,192,.3)', background: 'rgba(255,255,255,.06)', color: '#e6f0fa', fontSize: 15 }} />
                    </label>
                  )}
                  {pideIniciales && (
                    <label style={{ flex: '1 1 140px', fontSize: 12, color: '#7aa3c0' }}>
                      Initials / Iniciales *
                      <input id="firma-iniciales" value={initialsTyped ?? iniciales} maxLength={4}
                        onChange={(e) => setInitialsTyped(e.target.value.replace(/[^A-Za-zÀ-ÿ]/g, '').toUpperCase())}
                        style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 4, padding: '.65rem .85rem', borderRadius: 10, border: '1px solid rgba(122,163,192,.3)', background: 'rgba(255,255,255,.06)', color: '#e6f0fa', fontSize: 15 }} />
                      {iniciales && (
                        <span style={{ display: 'inline-block', marginTop: 6, background: '#fff', color: '#0a1628', borderRadius: 8, padding: '.15rem .7rem', fontFamily: fontCssFor(typedFontId), fontSize: 26 }}>{iniciales}</span>
                      )}
                    </label>
                  )}
                </div>
              </div>
            )}

            {/* Signature box — directly under the document, clearly labeled. */}
            <div style={{ marginTop: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: '.5rem', flexWrap: 'wrap' }}>
                <strong style={{ fontSize: 13, color: '#7aa3c0', textTransform: 'uppercase', letterSpacing: '.5px' }}>
                  Sign here
                </strong>
                {/* Draw / Type method toggle */}
                <div style={{ display: 'flex', gap: 4, background: 'rgba(122,163,192,.12)', padding: 3, borderRadius: 9 }}>
                  {[['draw', '✍️ Draw'], ['type', '⌨️ Type']].map(([m, label]) => (
                    <button key={m} onClick={() => setMode(m)} style={{ padding: '.35rem .85rem', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700, background: mode === m ? '#00d4ff' : 'transparent', color: mode === m ? '#0a1628' : '#7aa3c0' }}>{label}</button>
                  ))}
                </div>
              </div>

              {mode === 'draw' ? (
                <>
                  <div style={{ background: '#fff', borderRadius: 14, padding: 8, marginBottom: '.6rem', boxShadow: '0 4px 18px rgba(0,0,0,.3)', border: '2px dashed #00d4ff' }}>
                    <canvas
                      ref={canvasRef}
                      onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                      onTouchStart={start} onTouchMove={move} onTouchEnd={end}
                      style={{ display: 'block', width: '100%', height: 200, borderRadius: 8, touchAction: 'none', cursor: 'crosshair', background: '#fff' }}
                    />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.2rem' }}>
                    <div style={{ fontSize: 12, color: '#7aa3c0' }}>Sign with finger, stylus, or mouse</div>
                    <button onClick={clear} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'transparent', border: '1px solid rgba(122,163,192,.3)', color: '#7aa3c0', padding: '.4rem .8rem', borderRadius: 8, cursor: 'pointer', fontSize: 12 }}>
                      <Eraser size={12} /> Clear
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <input
                    value={typedName}
                    onChange={(e) => setTypedName(e.target.value)}
                    placeholder="Type your full name"
                    style={{ width: '100%', boxSizing: 'border-box', padding: '.7rem .9rem', borderRadius: 10, border: '1px solid rgba(122,163,192,.3)', background: 'rgba(255,255,255,.06)', color: '#e6f0fa', fontSize: 15, marginBottom: '.6rem' }}
                  />
                  {/* Pick one of three handwriting styles */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: '.6rem' }}>
                    {TYPE_FONTS.map((f) => (
                      <button key={f.id} onClick={() => setTypedFontId(f.id)} style={{ background: '#fff', color: '#0a1628', border: typedFontId === f.id ? '2px solid #00d4ff' : '2px solid transparent', borderRadius: 10, padding: '.55rem .35rem', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,.25)', overflow: 'hidden' }}>
                        <div style={{ fontFamily: f.css, fontSize: 20, lineHeight: 1.15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{typedName.trim() || 'Signature'}</div>
                        <div style={{ fontSize: 10, color: '#7aa3c0', marginTop: 3 }}>{f.label}</div>
                      </button>
                    ))}
                  </div>
                  {/* Big live preview = exactly what gets saved */}
                  <div style={{ background: '#fff', borderRadius: 14, padding: '1.3rem', marginBottom: '.6rem', boxShadow: '0 4px 18px rgba(0,0,0,.3)', border: '2px dashed #00d4ff', minHeight: 96, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {typedName.trim()
                      ? <span style={{ fontFamily: fontCssFor(typedFontId), fontSize: 44, color: '#0a1628', lineHeight: 1.1, textAlign: 'center', wordBreak: 'break-word' }}>{typedName.trim()}</span>
                      : <span style={{ color: '#9bb3c7', fontSize: 15 }}>Your signature preview appears here</span>}
                  </div>
                  <div style={{ fontSize: 12, color: '#7aa3c0', marginBottom: '1.2rem' }}>Pick a style above — this is exactly how your signature will appear.</div>
                </>
              )}

              <button
                onClick={submit}
                disabled={submitting}
                style={{
                  width: '100%', padding: '1rem', borderRadius: 12, border: 'none',
                  background: submitting ? 'rgba(0,212,255,.4)' : '#00d4ff', color: '#0a1628',
                  fontWeight: 800, fontSize: 16, cursor: submitting ? 'wait' : 'pointer',
                }}
              >
                {submitting ? 'Generating signed PDF…' : '✓ Sign & finish'}
              </button>

              <p style={{ fontSize: 11, color: '#45688a', textAlign: 'center', marginTop: '1.2rem' }}>
                By clicking "Sign & finish" you confirm you have reviewed the document above and agree this represents your legal signature.
              </p>
            </div>
          </>
        )}

        <div style={{ textAlign: 'center', color: '#45688a', fontSize: 11, marginTop: '2rem' }}>
          Powered by Deskte · Insurance Agent CRM
        </div>
      </div>
    </div>
  );
}


// Inline preview of the document. Iframe for PDF, <img> for image.
function DocumentPreview({ url, mime }) {
  const isPdf = (mime || '').toLowerCase().includes('pdf') || /\.pdf(\?|$)/i.test(url);
  const isImg = (mime || '').toLowerCase().startsWith('image/') || /\.(png|jpg|jpeg|webp|heic|gif)(\?|$)/i.test(url);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <FileText size={16} style={{ color: '#00d4ff' }} />
          <strong style={{ fontSize: 13, color: '#7aa3c0', textTransform: 'uppercase', letterSpacing: '.5px' }}>
            Document
          </strong>
        </div>
        <a href={url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#00d4ff', fontSize: 12, textDecoration: 'none' }}>
          <Download size={12} /> Open / download
        </a>
      </div>

      <div style={{
        background: '#fff',
        borderRadius: 12,
        overflow: 'hidden',
        boxShadow: '0 6px 24px rgba(0,0,0,.4)',
      }}>
        {isPdf ? (
          <iframe
            src={url}
            title="Document to sign"
            style={{ width: '100%', height: 720, border: 'none', display: 'block', background: '#fff' }}
          />
        ) : isImg ? (
          <img
            src={url}
            alt="Document to sign"
            style={{ width: '100%', display: 'block', maxHeight: 900, objectFit: 'contain', background: '#fff' }}
          />
        ) : (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <p style={{ color: '#0a1628' }}>This document type can't be previewed inline.</p>
            <a href={url} target="_blank" rel="noopener noreferrer"
              style={{ display: 'inline-block', padding: '.6rem 1rem', borderRadius: 8, background: '#00d4ff', color: '#0a1628', fontWeight: 700, textDecoration: 'none', marginTop: '.5rem' }}>
              Open document in new tab →
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

function SuccessScreen({ signedDocUrl, originalDocUrl }) {
  return (
    <div style={{ textAlign: 'center', padding: '2rem 1rem', background: 'rgba(0,255,136,.08)', border: '1px solid #00ff88', borderRadius: 14 }}>
      <CheckCircle size={48} style={{ color: '#00ff88' }} />
      <div style={{ fontWeight: 800, color: '#00ff88', marginTop: '.6rem', fontSize: '1.4rem' }}>
        Signed — thank you!
      </div>
      <div style={{ color: '#7aa3c0', fontSize: 14, marginTop: '.5rem', marginBottom: '1.5rem' }}>
        Your agent has been notified.
      </div>

      {signedDocUrl ? (
        <a
          href={signedDocUrl}
          target="_blank"
          rel="noopener noreferrer"
          download
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: '#00d4ff', color: '#0a1628',
            padding: '.9rem 1.4rem', borderRadius: 10,
            textDecoration: 'none', fontWeight: 800,
          }}
        >
          <Download size={16} /> Download your signed PDF
        </a>
      ) : originalDocUrl ? (
        <a
          href={originalDocUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#00d4ff', fontSize: 13 }}
        >
          View original document →
        </a>
      ) : null}

      <div style={{ fontSize: 11, color: '#45688a', marginTop: '1.5rem' }}>
        A copy has been sent to your agent.
      </div>
    </div>
  );
}

function CenteredMessage({ children }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0a1628', color: '#e6f0fa', padding: '2rem' }}>
      <div style={{ textAlign: 'center', maxWidth: 480 }}>{children}</div>
    </div>
  );
}

/**
 * Una pregunta del documento, tal como la ve el cliente.
 *
 * Los tipos son los mismos que en Aplicaciones a proposito: es el mismo cliente
 * contestando lo mismo, y dos vocabularios que hay que mantener a la vez acaban
 * separandose.
 *
 * `missing` pinta el borde en rojo cuando se intento firmar sin contestar una
 * obligatoria. Sin eso, en un cuestionario de quince preguntas el aviso dice
 * "falta una" y el cliente no sabe cual.
 */

export function QuestionField({ q, value, onChange, missing }) {
  const borde = missing ? '#ff3d6b' : 'rgba(122,163,192,.3)';
  const caja = {
    width: '100%', boxSizing: 'border-box', padding: '.6rem .7rem', borderRadius: 9,
    border: `1px solid ${borde}`, background: '#0d1c2e', color: '#eef6ff',
    fontSize: 14, fontFamily: 'inherit', outline: 'none',
  };
  const botonSiNo = (activo) => ({
    flex: 1, padding: '.65rem .5rem', borderRadius: 9, cursor: 'pointer',
    border: `1px solid ${activo ? '#34d399' : borde}`,
    background: activo ? 'rgba(52,211,153,.16)' : 'transparent',
    color: activo ? '#34d399' : '#7aa3c0', fontWeight: 700, fontSize: 14,
    fontFamily: 'inherit',
  });

  return (
    <div id={`q-${q.id}`}>
      <div style={{ fontSize: 14, color: '#eef6ff', marginBottom: '.45rem', lineHeight: 1.45 }}>
        {q.label}
        {q.required && <span style={{ color: '#ff3d6b', marginLeft: 4 }}>*</span>}
      </div>

      {q.type === 'yesno' && (
        <div style={{ display: 'flex', gap: 8 }}>
          {[['yes', 'Yes / Sí'], ['no', 'No']].map(([v, etiqueta]) => (
            <button key={v} type="button" onClick={() => onChange(v)} style={botonSiNo(value === v)}>{etiqueta}</button>
          ))}
        </div>
      )}

      {q.type === 'checkbox' && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: '#7aa3c0' }}>
          <input type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} />
          I agree / Acepto
        </label>
      )}

      {q.type === 'radio' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {(q.options || []).map((o) => (
            <label key={o} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: value === o ? '#eef6ff' : '#7aa3c0' }}>
              <input type="radio" name={q.id} checked={value === o} onChange={() => onChange(o)} />
              {o}
            </label>
          ))}
        </div>
      )}

      {q.type === 'select' && (
        <select value={value || ''} onChange={(e) => onChange(e.target.value)} style={caja}>
          <option value="">— Select —</option>
          {(q.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      )}

      {q.type !== 'yesno' && q.type !== 'checkbox' && q.type !== 'radio' && q.type !== 'select' && (
        <input value={value || ''} onChange={(e) => onChange(e.target.value)} style={caja} />
      )}
    </div>
  );
}
